import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Sparkles, Brain, Clock, Zap, Save, Loader2, FileText,
  Download, Eye, CheckCircle, AlertCircle, RefreshCw,
  Settings, Calendar, CreditCard, Mail, UserCheck, Bell
} from 'lucide-react';
import { getAccelerationOptions, DELIVERY_TIMES } from '../utils/accelerationHelpers';

const REPORT_TYPES = [
  {
    id: 'basic',
    name: 'Basic Report',
    credits: 150,
    icon: Sparkles,
    description: 'Standard AI analysis with key insights',
    color: 'sky'
  },
  {
    id: 'advanced',
    name: 'Advanced Report',
    credits: 1000,
    icon: Brain,
    description: 'Deep reasoning audit with comprehensive analysis',
    color: 'purple'
  }
];

const REPORT_ADDONS = [
  {
    id: 'credit',
    name: 'Credit Report',
    credits: 6000,
    icon: CreditCard,
    description: 'Full credit analysis with detailed borrower insights',
    color: 'amber'
  }
];

const IntakeAssistant = ({ userEmail, userMeta, userTier, onRefreshCredits }) => {
  // Configuration state
  const [config, setConfig] = useState({
    reportType: 'basic',
    deliverySpeed: 'regular',
    addons: [], // Array of addon ids
    autoTrigger: false,
    allowFollowUp: false,
    // Rate Limiting
    rateLimitHours: 24,
    maxTotalEmails: 10,
    // Follow-Up Limits
    maxFollowUps: 3,
    followUpIntervals: [3, 5, 7], // days
    // Email Options
    ccLoanOfficer: true, // CC loan officer's CloudMortgage account
    ccAdditionalEmails: [], // Additional CC emails
    // Escalation
    autoEscalateAfterMaxFollowUps: true,
    escalateOnHumanNeeded: true
  });
  const [savedConfig, setSavedConfig] = useState(null);
  const [isConfigLoading, setIsConfigLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Reports state
  const [reports, setReports] = useState([]);
  const [isReportsLoading, setIsReportsLoading] = useState(true);
  const [reportsError, setReportsError] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });

  const userCredits = userMeta?.credit || 0;

  // Normalize tier and get SLA based on membership (matching ReportGenerationModal)
  const normalizedTier = useMemo(() => (userTier?.toLowerCase() || 'free'), [userTier]);
  const standardDeliveryHours = useMemo(() => DELIVERY_TIMES[normalizedTier] || 9, [normalizedTier]);
  const accelerationOptions = useMemo(
    () => getAccelerationOptions(normalizedTier, userCredits),
    [normalizedTier, userCredits]
  );

  // Build delivery options dynamically based on tier
  const deliveryOptions = useMemo(() => {
    // Standard delivery option (based on user's tier)
    const options = [{
      id: 'regular',
      level: normalizedTier,
      label: 'Standard Delivery',
      hours: standardDeliveryHours,
      credits: 0,
      timeSaved: 0,
      canAfford: true
    }];

    // Add acceleration options from the helper
    accelerationOptions.forEach(opt => {
      options.push({
        id: opt.level,
        level: opt.level,
        label: opt.label,
        hours: opt.hours,
        credits: opt.credits,
        timeSaved: opt.timeSaved,
        canAfford: opt.canAfford
      });
    });

    return options;
  }, [normalizedTier, standardDeliveryHours, accelerationOptions]);

  // Check if config has unsaved changes
  const hasUnsavedChanges = !savedConfig || (
    config.reportType !== savedConfig.reportType ||
    config.deliverySpeed !== savedConfig.deliverySpeed ||
    config.autoTrigger !== savedConfig.autoTrigger ||
    config.allowFollowUp !== savedConfig.allowFollowUp ||
    config.rateLimitHours !== savedConfig.rateLimitHours ||
    config.maxTotalEmails !== savedConfig.maxTotalEmails ||
    config.maxFollowUps !== savedConfig.maxFollowUps ||
    JSON.stringify(config.followUpIntervals || []) !== JSON.stringify(savedConfig.followUpIntervals || []) ||
    config.ccLoanOfficer !== savedConfig.ccLoanOfficer ||
    JSON.stringify(config.ccAdditionalEmails || []) !== JSON.stringify(savedConfig.ccAdditionalEmails || []) ||
    config.autoEscalateAfterMaxFollowUps !== savedConfig.autoEscalateAfterMaxFollowUps ||
    config.escalateOnHumanNeeded !== savedConfig.escalateOnHumanNeeded ||
    JSON.stringify(config.addons || []) !== JSON.stringify(savedConfig.addons || [])
  );

  // Fetch configuration on mount
  const fetchConfig = useCallback(async () => {
    setIsConfigLoading(true);
    try {
      const response = await fetch('/api/intake-assistant/config', {
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        if (data.data) {
          const loadedConfig = {
            reportType: data.data.reportType || 'basic',
            deliverySpeed: data.data.deliverySpeed || 'regular',
            addons: data.data.addons || [],
            autoTrigger: data.data.autoTrigger || false,
            allowFollowUp: data.data.allowFollowUp || false,
            // Rate Limiting
            rateLimitHours: data.data.rateLimitHours ?? 24,
            maxTotalEmails: data.data.maxTotalEmails ?? 10,
            // Follow-Up Limits
            maxFollowUps: data.data.maxFollowUps ?? 3,
            followUpIntervals: data.data.followUpIntervals || [3, 5, 7],
            // Email Options
            ccLoanOfficer: data.data.ccLoanOfficer ?? true,
            ccAdditionalEmails: data.data.ccAdditionalEmails || [],
            // Escalation
            autoEscalateAfterMaxFollowUps: data.data.autoEscalateAfterMaxFollowUps ?? true,
            escalateOnHumanNeeded: data.data.escalateOnHumanNeeded ?? true
          };
          setConfig(loadedConfig);
          setSavedConfig(loadedConfig);
        }
      }
    } catch (err) {
      console.error('Failed to fetch intake config:', err);
      // Use default config on error
      setSavedConfig(config);
    } finally {
      setIsConfigLoading(false);
    }
  }, []);

  // Fetch reports on mount
  const fetchReports = useCallback(async (page = 1) => {
    setIsReportsLoading(true);
    setReportsError(null);
    try {
      const response = await fetch(`/api/intake-assistant/reports?page=${page}&limit=${pagination.limit}`, {
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setReports(data.data?.reports || []);
        setPagination(prev => ({
          ...prev,
          page: data.data?.pagination?.page || page,
          total: data.data?.pagination?.total || 0,
          totalPages: data.data?.pagination?.totalPages || 0
        }));
      } else {
        // No reports found or API not available yet - show empty state
        setReports([]);
      }
    } catch (err) {
      console.error('Failed to fetch intake reports:', err);
      setReportsError('Failed to load reports');
      setReports([]);
    } finally {
      setIsReportsLoading(false);
    }
  }, [pagination.limit]);

  useEffect(() => {
    fetchConfig();
    fetchReports();
  }, [fetchConfig, fetchReports]);

  // Save configuration
  const handleSaveConfig = async () => {
    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      const response = await fetch('/api/intake-assistant/config', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(config)
      });

      if (response.ok) {
        setSavedConfig({ ...config });
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        throw new Error('Failed to save configuration');
      }
    } catch (err) {
      console.error('Failed to save config:', err);
      setSaveError('Failed to save configuration. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle report view
  const handleViewReport = (report) => {
    if (report.reportUrl) {
      window.open(report.reportUrl, '_blank');
    }
  };

  // Handle report download
  const handleDownloadReport = async (report) => {
    if (!report.id) return;

    try {
      const response = await fetch(`/api/intake-assistant/reports/${report.id}/download`, {
        credentials: 'include'
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `intake-report-${report.caseNumber || report.id}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (err) {
      console.error('Failed to download report:', err);
    }
  };

  // Get selected report type and delivery details
  const selectedReportType = REPORT_TYPES.find(r => r.id === config.reportType);
  const selectedDelivery = deliveryOptions.find(d => d.id === config.deliverySpeed) || deliveryOptions[0];
  const addonsCredits = (config.addons || []).reduce((sum, addonId) => {
    const addon = REPORT_ADDONS.find(a => a.id === addonId);
    return sum + (addon?.credits || 0);
  }, 0);
  const totalCreditsPerReport = (selectedReportType?.credits || 0) + (selectedDelivery?.credits || 0) + addonsCredits;
  const deliveryHours = selectedDelivery?.hours || standardDeliveryHours;

  // Get status badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { bg: 'bg-amber-500/20', text: 'text-amber-400', icon: Clock },
      running: { bg: 'bg-sky-500/20', text: 'text-sky-400', icon: Loader2 },
      completed: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', icon: CheckCircle },
      failed: { bg: 'bg-red-500/20', text: 'text-red-400', icon: AlertCircle }
    };
    const cfg = statusConfig[status] || statusConfig.pending;
    const Icon = cfg.icon;
    return (
      <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${cfg.bg} ${cfg.text}`}>
        <Icon className={`w-3 h-3 ${status === 'running' ? 'animate-spin' : ''}`} />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  // Format tier name for display
  const getTierDisplayName = (tier) => {
    const names = { free: 'Free', starter: 'Starter', pro: 'Pro', ultra: 'Ultra' };
    return names[tier?.toLowerCase()] || tier || 'Free';
  };

  return (
    <div className="flex-1 flex flex-col min-h-0 p-6 overflow-y-auto">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-xl font-bold dark:text-white text-slate-900">Intake Assistant</h2>
        <p className="text-sm text-slate-500 mt-1">
          Configure automatic report generation when borrowers complete their loan application.
        </p>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 flex-1 min-h-0">
        {/* Left: Workflow Configuration */}
        <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-xl overflow-hidden flex flex-col">
          <div className="p-4 border-b dark:border-slate-800 border-slate-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Settings className="w-5 h-5 dark:text-sky-400 text-sky-600" />
              <h3 className="font-semibold dark:text-white text-slate-900">Workflow Configuration</h3>
            </div>
            {hasUnsavedChanges && (
              <span className="text-xs text-amber-500 font-medium">Unsaved changes</span>
            )}
            {saveSuccess && (
              <span className="text-xs text-emerald-500 font-medium flex items-center gap-1">
                <CheckCircle className="w-3 h-3" /> Saved
              </span>
            )}
          </div>

          <div className="p-4 space-y-6 flex-1 overflow-y-auto">
            {isConfigLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin dark:text-sky-400 text-sky-600" />
              </div>
            ) : (
              <>
                {/* Report Type Selection */}
                <div>
                  <h4 className="text-sm font-semibold dark:text-white text-slate-900 mb-3 flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full dark:bg-sky-500/20 bg-sky-100 dark:text-sky-400 text-sky-600 text-xs flex items-center justify-center">1</span>
                    Report Type
                  </h4>
                  <div className="grid grid-cols-1 gap-3">
                    {REPORT_TYPES.map(report => {
                      const Icon = report.icon;
                      const isSelected = config.reportType === report.id;
                      const colorClasses = report.color === 'sky'
                        ? 'border-sky-500/50 dark:bg-sky-500/10 bg-sky-50 shadow-[0_0_20px_rgba(56,189,248,0.2)]'
                        : 'border-purple-500/50 dark:bg-purple-500/10 bg-purple-50 shadow-[0_0_20px_rgba(168,85,247,0.2)]';

                      return (
                        <button
                          key={report.id}
                          onClick={() => setConfig(prev => ({ ...prev, reportType: report.id }))}
                          className={`p-4 rounded-xl border-2 transition-all text-left ${isSelected
                            ? colorClasses
                            : 'dark:border-slate-700 border-slate-200 dark:bg-slate-800/50 bg-white hover:border-slate-300 dark:hover:border-slate-600'
                            }`}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${report.color === 'sky' ? 'dark:bg-sky-500/20 bg-sky-100' : 'dark:bg-purple-500/20 bg-purple-100'}`}>
                              <Icon className={`w-5 h-5 ${report.color === 'sky' ? 'dark:text-sky-400 text-sky-600' : 'dark:text-purple-400 text-purple-600'}`} />
                            </div>
                            <div className="flex-1">
                              <p className="font-semibold dark:text-white text-slate-900">{report.name}</p>
                              <p className="text-xs text-slate-500 mt-1">{report.description}</p>
                              <p className="text-sm font-bold dark:text-sky-400 text-sky-600 mt-2">{report.credits} credits</p>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Standard Delivery Time Info */}
                <div className="border dark:border-amber-500/30 border-amber-200 rounded-xl p-4 bg-gradient-to-br from-amber-500/5 dark:via-slate-900/50 via-white to-white dark:to-slate-900/50">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-xl dark:bg-amber-500/20 bg-amber-100 border dark:border-amber-400/30 border-amber-200 flex items-center justify-center">
                      <Clock className="w-5 h-5 dark:text-amber-400 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold dark:text-white text-slate-900">Standard Delivery Time</p>
                      <p className="text-xs text-slate-500">Your {getTierDisplayName(normalizedTier)} membership</p>
                    </div>
                  </div>
                  <div className="text-center py-3 px-4 rounded-lg dark:bg-slate-950/50 bg-white border dark:border-slate-800 border-amber-100">
                    <p className="text-2xl font-bold dark:text-amber-400 text-amber-600">{standardDeliveryHours} hours</p>
                    <p className="text-xs text-slate-500 mt-1">
                      Based on your membership tier
                    </p>
                  </div>
                </div>

                {/* Delivery Speed Selection */}
                <div>
                  <h4 className="text-sm font-semibold dark:text-white text-slate-900 mb-3 flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full dark:bg-sky-500/20 bg-sky-100 dark:text-sky-400 text-sky-600 text-xs flex items-center justify-center">2</span>
                    Speed Up Delivery? (Optional)
                  </h4>
                  <div className="space-y-2">
                    {deliveryOptions.map(option => {
                      const isSelected = config.deliverySpeed === option.id;
                      const isStandard = option.id === 'regular';
                      const canSelect = isStandard || option.canAfford;

                      return (
                        <button
                          key={option.id}
                          onClick={() => canSelect && setConfig(prev => ({ ...prev, deliverySpeed: option.id }))}
                          disabled={!canSelect}
                          className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all ${isSelected
                            ? isStandard
                              ? 'border-emerald-500/50 dark:bg-emerald-500/10 bg-emerald-50'
                              : 'border-sky-500/50 dark:bg-sky-500/10 bg-sky-50 shadow-[0_0_20px_rgba(56,189,248,0.15)]'
                            : canSelect
                              ? 'dark:border-slate-700 border-slate-200 dark:bg-slate-800/50 bg-white hover:border-slate-300 dark:hover:border-slate-600'
                              : 'dark:border-slate-700 border-slate-200 dark:bg-slate-800/50 bg-white opacity-50 cursor-not-allowed'
                            }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="flex items-center gap-2">
                                {!isStandard && <Zap className="w-4 h-4 dark:text-sky-400 text-sky-600" />}
                                <p className="text-sm font-semibold dark:text-white text-slate-900">{option.label}</p>
                              </div>
                              <p className="text-xs text-slate-500 mt-0.5">
                                {isStandard ? (
                                  <>{option.hours} hours • No extra cost</>
                                ) : (
                                  <>Get it in <span className="dark:text-sky-400 text-sky-600 font-semibold">{option.hours}h</span> (save {option.timeSaved}h)</>
                                )}
                              </p>
                            </div>
                            <div className="text-right">
                              {option.credits > 0 ? (
                                <>
                                  <p className="text-sm font-bold dark:text-sky-400 text-sky-600">+{option.credits}</p>
                                  <p className="text-[10px] text-slate-500 uppercase tracking-wide">credits</p>
                                </>
                              ) : (
                                <span className="dark:text-emerald-400 text-emerald-600 text-xs font-semibold">FREE</span>
                              )}
                            </div>
                          </div>
                          {!isStandard && !option.canAfford && (
                            <p className="text-[10px] text-red-500 mt-2">Insufficient credits</p>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Add-ons Selection */}
                <div>
                  <h4 className="text-sm font-semibold dark:text-white text-slate-900 mb-3 flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full dark:bg-sky-500/20 bg-sky-100 dark:text-sky-400 text-sky-600 text-xs flex items-center justify-center">3</span>
                    Add-ons (Optional)
                  </h4>
                  <div className="space-y-2">
                    {REPORT_ADDONS.map(addon => {
                      const Icon = addon.icon;
                      const isSelected = (config.addons || []).includes(addon.id);
                      const canAfford = userCredits >= addon.credits;

                      const toggleAddon = () => {
                        setConfig(prev => {
                          const currentAddons = prev.addons || [];
                          if (currentAddons.includes(addon.id)) {
                            return { ...prev, addons: currentAddons.filter(id => id !== addon.id) };
                          } else if (canAfford) {
                            return { ...prev, addons: [...currentAddons, addon.id] };
                          }
                          return prev;
                        });
                      };

                      return (
                        <button
                          key={addon.id}
                          onClick={toggleAddon}
                          disabled={!isSelected && !canAfford}
                          className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all ${isSelected
                            ? 'border-amber-500/50 dark:bg-amber-500/10 bg-amber-50 shadow-[0_0_20px_rgba(245,158,11,0.15)]'
                            : canAfford
                              ? 'dark:border-slate-700 border-slate-200 dark:bg-slate-800/50 bg-white hover:border-slate-300 dark:hover:border-slate-600'
                              : 'dark:border-slate-700 border-slate-200 dark:bg-slate-800/50 bg-white opacity-50 cursor-not-allowed'
                            }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isSelected ? 'dark:bg-amber-500/20 bg-amber-100' : 'dark:bg-slate-700 bg-slate-100'}`}>
                                <Icon className={`w-4 h-4 ${isSelected ? 'dark:text-amber-400 text-amber-600' : 'dark:text-slate-400 text-slate-500'}`} />
                              </div>
                              <div>
                                <p className="text-sm font-semibold dark:text-white text-slate-900">{addon.name}</p>
                                <p className="text-xs text-slate-500">{addon.description}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-bold dark:text-amber-400 text-amber-600">+{addon.credits.toLocaleString()}</p>
                              <p className="text-[10px] text-slate-500 uppercase tracking-wide">credits</p>
                            </div>
                          </div>
                          {!isSelected && !canAfford && (
                            <p className="text-[10px] text-red-500 mt-2">Insufficient credits</p>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Automation Toggles */}
                <div>
                  <h4 className="text-sm font-semibold dark:text-white text-slate-900 mb-3 flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full dark:bg-sky-500/20 bg-sky-100 dark:text-sky-400 text-sky-600 text-xs flex items-center justify-center">4</span>
                    Automation
                  </h4>
                  <div className="space-y-3">
                    {/* Auto-trigger Toggle */}
                    <div className="p-4 rounded-xl border dark:border-slate-700 border-slate-200 dark:bg-slate-800/50 bg-slate-50">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium dark:text-white text-slate-900">Auto-trigger on intake completion</p>
                          <p className="text-xs text-slate-500 mt-1">
                            Automatically generate reports when borrowers submit their application
                          </p>
                        </div>
                        <button
                          onClick={() => setConfig(prev => ({ ...prev, autoTrigger: !prev.autoTrigger }))}
                          className={`relative w-12 h-6 rounded-full transition-colors ${config.autoTrigger
                            ? 'bg-sky-600'
                            : 'dark:bg-slate-700 bg-slate-300'
                            }`}
                        >
                          <span
                            className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${config.autoTrigger ? 'left-7' : 'left-1'
                              }`}
                          />
                        </button>
                      </div>
                    </div>

                    {/* Allow Follow Up Toggle */}
                    <div className="p-4 rounded-xl border dark:border-slate-700 border-slate-200 dark:bg-slate-800/50 bg-slate-50">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium dark:text-white text-slate-900">Allow Follow Up</p>
                          <p className="text-xs text-slate-500 mt-1">
                            Enable AI to send follow-up messages to borrowers for missing information
                          </p>
                        </div>
                        <button
                          onClick={() => setConfig(prev => ({ ...prev, allowFollowUp: !prev.allowFollowUp }))}
                          className={`relative w-12 h-6 rounded-full transition-colors ${config.allowFollowUp
                            ? 'bg-sky-600'
                            : 'dark:bg-slate-700 bg-slate-300'
                            }`}
                        >
                          <span
                            className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${config.allowFollowUp ? 'left-7' : 'left-1'
                              }`}
                          />
                        </button>
                      </div>
                    </div>

                    {/* Follow-Up Configuration (shown when Allow Follow Up is enabled) */}
                    {config.allowFollowUp && (
                      <div className="space-y-4 pt-2 border-t dark:border-slate-700 border-slate-200 mt-4">
                        {/* Rate Limiting */}
                        <div className="p-4 rounded-xl border dark:border-slate-700 border-slate-200 dark:bg-slate-800/30 bg-slate-50">
                          <div className="flex items-center gap-2 mb-3">
                            <Mail className="w-4 h-4 dark:text-sky-400 text-sky-600" />
                            <p className="font-medium dark:text-white text-slate-900 text-sm">Rate Limiting</p>
                          </div>
                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-sm dark:text-slate-300 text-slate-700">Cooldown between emails</p>
                                <p className="text-xs text-slate-500">Minimum hours between sending emails</p>
                              </div>
                              <div className="flex items-center gap-2">
                                <input
                                  type="number"
                                  min="1"
                                  max="72"
                                  value={config.rateLimitHours}
                                  onChange={(e) => setConfig(prev => ({ ...prev, rateLimitHours: parseInt(e.target.value) || 24 }))}
                                  className="w-16 px-2 py-1 text-sm rounded-lg border dark:border-slate-600 border-slate-300 dark:bg-slate-700 bg-white dark:text-white text-slate-900 text-center"
                                />
                                <span className="text-xs text-slate-500">hours</span>
                              </div>
                            </div>
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-sm dark:text-slate-300 text-slate-700">Max emails per case</p>
                                <p className="text-xs text-slate-500">Total email limit for a single case</p>
                              </div>
                              <div className="flex items-center gap-2">
                                <input
                                  type="number"
                                  min="1"
                                  max="50"
                                  value={config.maxTotalEmails}
                                  onChange={(e) => setConfig(prev => ({ ...prev, maxTotalEmails: parseInt(e.target.value) || 10 }))}
                                  className="w-16 px-2 py-1 text-sm rounded-lg border dark:border-slate-600 border-slate-300 dark:bg-slate-700 bg-white dark:text-white text-slate-900 text-center"
                                />
                                <span className="text-xs text-slate-500">emails</span>
                              </div>
                            </div>
                            {/* CC Recipients */}
                            <div className="pt-3 border-t dark:border-slate-700 border-slate-200">
                              <p className="text-sm dark:text-slate-300 text-slate-700 mb-2">CC Recipients</p>
                              <p className="text-xs text-slate-500 mb-3">Include these recipients on all follow-up emails</p>

                              {/* Loan Officer Account */}
                              <label className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700/50 cursor-pointer mb-2">
                                <input
                                  type="checkbox"
                                  checked={config.ccLoanOfficer}
                                  onChange={(e) => setConfig(prev => ({ ...prev, ccLoanOfficer: e.target.checked }))}
                                  className="w-4 h-4 rounded border-slate-300 dark:border-slate-600 text-sky-600 focus:ring-sky-500"
                                />
                                <div className="flex-1">
                                  <p className="text-sm dark:text-white text-slate-900">Loan Officer (CloudMortgage.io account)</p>
                                  <p className="text-xs text-slate-500">{userEmail || 'Your account email'}</p>
                                </div>
                              </label>

                              {/* Additional Emails */}
                              <div className="space-y-2">
                                {(config.ccAdditionalEmails || []).map((email, idx) => (
                                  <div key={idx} className="flex items-center gap-2">
                                    <input
                                      type="email"
                                      value={email}
                                      onChange={(e) => {
                                        const newEmails = [...(config.ccAdditionalEmails || [])];
                                        newEmails[idx] = e.target.value;
                                        setConfig(prev => ({ ...prev, ccAdditionalEmails: newEmails }));
                                      }}
                                      placeholder="email@example.com"
                                      className="flex-1 px-3 py-1.5 text-sm rounded-lg border dark:border-slate-600 border-slate-300 dark:bg-slate-700 bg-white dark:text-white text-slate-900"
                                    />
                                    <button
                                      onClick={() => {
                                        const newEmails = (config.ccAdditionalEmails || []).filter((_, i) => i !== idx);
                                        setConfig(prev => ({ ...prev, ccAdditionalEmails: newEmails }));
                                      }}
                                      className="p-1.5 rounded-lg text-red-500 hover:bg-red-500/10 transition-colors"
                                    >
                                      <AlertCircle className="w-4 h-4" />
                                    </button>
                                  </div>
                                ))}
                                <button
                                  onClick={() => setConfig(prev => ({
                                    ...prev,
                                    ccAdditionalEmails: [...(prev.ccAdditionalEmails || []), '']
                                  }))}
                                  className="w-full px-3 py-1.5 text-sm rounded-lg border-2 border-dashed dark:border-slate-600 border-slate-300 dark:text-slate-400 text-slate-500 hover:border-sky-500 hover:text-sky-500 transition-colors"
                                >
                                  + Add another email
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Follow-Up Limits */}
                        <div className="p-4 rounded-xl border dark:border-slate-700 border-slate-200 dark:bg-slate-800/30 bg-slate-50">
                          <div className="flex items-center gap-2 mb-3">
                            <Clock className="w-4 h-4 dark:text-amber-400 text-amber-600" />
                            <p className="font-medium dark:text-white text-slate-900 text-sm">Follow-Up Limits</p>
                          </div>
                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-sm dark:text-slate-300 text-slate-700">Max automated follow-ups</p>
                                <p className="text-xs text-slate-500">Number of follow-up emails before stopping</p>
                              </div>
                              <div className="flex items-center gap-2">
                                <input
                                  type="number"
                                  min="1"
                                  max="10"
                                  value={config.maxFollowUps}
                                  onChange={(e) => setConfig(prev => ({ ...prev, maxFollowUps: parseInt(e.target.value) || 3 }))}
                                  className="w-16 px-2 py-1 text-sm rounded-lg border dark:border-slate-600 border-slate-300 dark:bg-slate-700 bg-white dark:text-white text-slate-900 text-center"
                                />
                                <span className="text-xs text-slate-500">times</span>
                              </div>
                            </div>
                            <div>
                              <div className="flex items-center justify-between mb-2">
                                <p className="text-sm dark:text-slate-300 text-slate-700">Follow-up intervals</p>
                                <p className="text-xs text-slate-500">Days between each follow-up</p>
                              </div>
                              <div className="flex items-center gap-2">
                                {(config.followUpIntervals || [3, 5, 7]).map((interval, idx) => (
                                  <div key={idx} className="flex items-center gap-1">
                                    <input
                                      type="number"
                                      min="1"
                                      max="30"
                                      value={interval}
                                      onChange={(e) => {
                                        const newIntervals = [...(config.followUpIntervals || [3, 5, 7])];
                                        newIntervals[idx] = parseInt(e.target.value) || 1;
                                        setConfig(prev => ({ ...prev, followUpIntervals: newIntervals }));
                                      }}
                                      className="w-12 px-2 py-1 text-sm rounded-lg border dark:border-slate-600 border-slate-300 dark:bg-slate-700 bg-white dark:text-white text-slate-900 text-center"
                                    />
                                    {idx < (config.followUpIntervals || [3, 5, 7]).length - 1 && (
                                      <span className="text-slate-400">→</span>
                                    )}
                                  </div>
                                ))}
                                <span className="text-xs text-slate-500 ml-1">days</span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Escalation Triggers */}
                        <div className="p-4 rounded-xl border dark:border-slate-700 border-slate-200 dark:bg-slate-800/30 bg-slate-50">
                          <div className="flex items-center gap-2 mb-3">
                            <Bell className="w-4 h-4 dark:text-red-400 text-red-600" />
                            <p className="font-medium dark:text-white text-slate-900 text-sm">Escalation Triggers</p>
                          </div>
                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-sm dark:text-slate-300 text-slate-700">Auto-escalate after max follow-ups</p>
                                <p className="text-xs text-slate-500">Escalate to loan officer when follow-ups exhausted</p>
                              </div>
                              <button
                                onClick={() => setConfig(prev => ({ ...prev, autoEscalateAfterMaxFollowUps: !prev.autoEscalateAfterMaxFollowUps }))}
                                className={`relative w-10 h-5 rounded-full transition-colors ${config.autoEscalateAfterMaxFollowUps
                                  ? 'bg-sky-600'
                                  : 'dark:bg-slate-700 bg-slate-300'
                                  }`}
                              >
                                <span
                                  className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${config.autoEscalateAfterMaxFollowUps ? 'left-5' : 'left-0.5'
                                    }`}
                                />
                              </button>
                            </div>
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-sm dark:text-slate-300 text-slate-700">Escalate when human response needed</p>
                                <p className="text-xs text-slate-500">Auto-escalate complex inquiries requiring human attention</p>
                              </div>
                              <button
                                onClick={() => setConfig(prev => ({ ...prev, escalateOnHumanNeeded: !prev.escalateOnHumanNeeded }))}
                                className={`relative w-10 h-5 rounded-full transition-colors ${config.escalateOnHumanNeeded
                                  ? 'bg-sky-600'
                                  : 'dark:bg-slate-700 bg-slate-300'
                                  }`}
                              >
                                <span
                                  className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${config.escalateOnHumanNeeded ? 'left-5' : 'left-0.5'
                                    }`}
                                />
                              </button>
                            </div>
                            <div className="pt-2 border-t dark:border-slate-700 border-slate-200">
                              <p className="text-xs text-slate-500 flex items-center gap-1">
                                <UserCheck className="w-3 h-3" />
                                Manual escalation is always available for loan officers
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Summary */}
                <div className="p-4 rounded-xl border dark:border-slate-700 border-slate-200 dark:bg-slate-800/30 bg-slate-50">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-slate-500">Report Type:</span>
                    <span className="text-sm font-semibold dark:text-white text-slate-900">{selectedReportType?.name}</span>
                  </div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-slate-500">Delivery Time:</span>
                    <span className="text-sm font-semibold dark:text-amber-400 text-amber-600">{deliveryHours} hours</span>
                  </div>
                  {(config.addons || []).length > 0 && (
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-slate-500">Add-ons:</span>
                      <span className="text-sm font-semibold dark:text-amber-400 text-amber-600">
                        {(config.addons || []).map(id => REPORT_ADDONS.find(a => a.id === id)?.name).join(', ')}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-slate-500">Credits per report:</span>
                    <span className="text-lg font-bold dark:text-white text-slate-900">{totalCreditsPerReport.toLocaleString()}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs pt-2 border-t dark:border-slate-700 border-slate-200">
                    <span className="text-slate-500">Your balance:</span>
                    <span className="text-slate-400">{userCredits.toLocaleString()} credits</span>
                  </div>
                </div>

                {saveError && (
                  <div className="p-3 rounded-lg dark:bg-red-500/10 bg-red-50 border dark:border-red-500/30 border-red-200">
                    <p className="text-sm dark:text-red-300 text-red-700">{saveError}</p>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Save Button */}
          <div className="p-4 border-t dark:border-slate-800 border-slate-200">
            <button
              onClick={handleSaveConfig}
              disabled={isSaving || isConfigLoading || !hasUnsavedChanges || saveSuccess}
              className={`w-full px-4 py-2.5 rounded-xl font-medium transition-all flex items-center justify-center gap-2 ${
                saveSuccess
                  ? 'bg-emerald-600 text-white'
                  : hasUnsavedChanges
                    ? 'bg-sky-600 text-white hover:bg-sky-500'
                    : 'dark:bg-slate-800 bg-slate-100 text-slate-500 cursor-not-allowed'
                }`}
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : saveSuccess ? (
                <>
                  <CheckCircle className="w-4 h-4" />
                  Saved Successfully!
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save Configuration
                </>
              )}
            </button>
          </div>
        </div>

        {/* Right: Generated Reports */}
        <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-xl overflow-hidden flex flex-col">
          <div className="p-4 border-b dark:border-slate-800 border-slate-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 dark:text-sky-400 text-sky-600" />
              <h3 className="font-semibold dark:text-white text-slate-900">Generated Reports</h3>
              {reports.length > 0 && (
                <span className="text-xs dark:bg-slate-800 bg-slate-100 dark:text-slate-400 text-slate-600 px-2 py-0.5 rounded-full">
                  {pagination.total}
                </span>
              )}
            </div>
            <button
              onClick={() => fetchReports(1)}
              disabled={isReportsLoading}
              className="p-2 rounded-lg dark:hover:bg-slate-800 hover:bg-slate-100 transition-colors"
            >
              <RefreshCw className={`w-4 h-4 dark:text-slate-400 text-slate-600 ${isReportsLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            {isReportsLoading && reports.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin dark:text-sky-400 text-sky-600" />
              </div>
            ) : reportsError ? (
              <div className="text-center py-12">
                <AlertCircle className="w-8 h-8 dark:text-red-400 text-red-500 mx-auto mb-2" />
                <p className="text-sm text-slate-500">{reportsError}</p>
                <button
                  onClick={() => fetchReports(1)}
                  className="mt-3 text-sm text-sky-600 hover:text-sky-500"
                >
                  Try again
                </button>
              </div>
            ) : reports.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 dark:text-slate-700 text-slate-300 mx-auto mb-3" />
                <p className="font-medium dark:text-white text-slate-900">No reports yet</p>
                <p className="text-sm text-slate-500 mt-1">
                  Reports will appear here when borrowers complete their intake
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {reports.map(report => (
                  <div
                    key={report.id}
                    className="p-4 rounded-xl border dark:border-slate-700 border-slate-200 dark:bg-slate-800/50 bg-slate-50 hover:border-slate-300 dark:hover:border-slate-600 transition-colors"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="font-medium dark:text-white text-slate-900">
                          {report.borrowerName || `Case #${report.caseNumber}`}
                        </p>
                        <p className="text-xs text-slate-500">{report.caseNumber}</p>
                      </div>
                      {getStatusBadge(report.status)}
                    </div>
                    <div className="flex items-center gap-4 text-xs text-slate-500 mb-3">
                      <span className="flex items-center gap-1">
                        {report.reportType === 'basic' ? (
                          <Sparkles className="w-3 h-3" />
                        ) : (
                          <Brain className="w-3 h-3" />
                        )}
                        {report.reportType === 'basic' ? 'Basic' : 'Advanced'}
                      </span>
                      {(report.addons?.includes('credit') || report.reportType === 'credit') && (
                        <span className="flex items-center gap-1 dark:text-amber-400 text-amber-600">
                          <CreditCard className="w-3 h-3" />
                          +Credit
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(report.generatedAt).toLocaleDateString()}
                      </span>
                    </div>
                    {report.status === 'completed' && (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleViewReport(report)}
                          className="flex-1 px-3 py-1.5 rounded-lg text-xs font-medium dark:bg-slate-700 bg-slate-200 dark:text-white text-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors flex items-center justify-center gap-1"
                        >
                          <Eye className="w-3 h-3" />
                          View
                        </button>
                        <button
                          onClick={() => handleDownloadReport(report)}
                          className="flex-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-sky-600 text-white hover:bg-sky-500 transition-colors flex items-center justify-center gap-1"
                        >
                          <Download className="w-3 h-3" />
                          Download
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="p-4 border-t dark:border-slate-800 border-slate-200 flex items-center justify-between">
              <button
                onClick={() => fetchReports(pagination.page - 1)}
                disabled={pagination.page <= 1 || isReportsLoading}
                className="px-3 py-1.5 rounded-lg text-sm dark:bg-slate-800 bg-slate-100 dark:text-white text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="text-sm text-slate-500">
                Page {pagination.page} of {pagination.totalPages}
              </span>
              <button
                onClick={() => fetchReports(pagination.page + 1)}
                disabled={pagination.page >= pagination.totalPages || isReportsLoading}
                className="px-3 py-1.5 rounded-lg text-sm dark:bg-slate-800 bg-slate-100 dark:text-white text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default IntakeAssistant;
